import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import {
  MainHeadline,
  PageWrapper,
  PageFooter,
  Flex,
} from '../../../styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { NavBar } from '../../../components/NavBar/NavBar'
import { Options } from 'components/Options/Options'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearInventorySuppliersList,
  deleteSupplier,
  getSuppliers,
} from 'features/supliers/suppliersSlice'
import { AppDispatch, RootState } from 'store'
import { Pagination } from 'components/Pagination/Pagination'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { setConfirmModal } from 'features/global/globalSlice'
import { getParams, handlePagination } from 'utils/helpers'
import { ModalTypes } from 'types/global.interface'
import useQueryPagination from 'hooks/useQueryPagination'
import { IInventorySupplierCommercials } from 'types/inventorySupply.interface'
import {
  formatSupplyGhs,
  getInventorySupplierCommercials,
  supplierLeadDeltaDays,
  supplierLeadDeltaTone,
  supplierOnTimeTone,
} from '../inventorySupplyDemoData'
import {
  SupplyCellValue,
  SupplyLeadDelta,
  SupplyLeadWrap,
  SupplyNoteText,
  SupplyOpenOrders,
} from './InvetorySuppliers.styled'

const leadDeltaLabel = (c: IInventorySupplierCommercials): string => {
  const delta = supplierLeadDeltaDays(c)
  if (delta === 0) return 'on promise'
  return `${delta > 0 ? '+' : ''}${delta}d vs promised ${c.promisedLeadTimeDays}d`
}

export const InvetorySuppliers = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { suppliers, totalPages, isLoading } = useSelector((state: RootState) => state.suppliers)
  const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))

  useQueryPagination({ fetchFunctions: [getSuppliers], cleanUpFunctions: [clearInventorySuppliersList], params })

  const statuses = ['Active', 'Inactive']

  const rows = useMemo(() => {
    return (suppliers ?? []).map((item) => ({
      ...item,
      commercials: getInventorySupplierCommercials(item.name, item.commercials),
    }))
  }, [suppliers])

  const handleRemove = (id: number) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(deleteSupplier(id))
        },
        word: 'delete supplier',
        setParams: {
          type: ModalTypes.Delete,
          f: setParams,
          noItems: suppliers.length < 2,
        },
      }),
    )
  }

  const columns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'Name', cell: (item) => item.name || '/' },
    {
      id: 'leadTime',
      header: 'AVG LEAD TIME',
      cell: (item) => (item.commercials ? (
        <SupplyLeadWrap>
          <SupplyCellValue>
            {`${item.commercials.avgLeadTimeDays}d `}
            <SupplyLeadDelta $tone={supplierLeadDeltaTone(item.commercials)}>
              {leadDeltaLabel(item.commercials)}
            </SupplyLeadDelta>
          </SupplyCellValue>
          {item.commercials.note && (
            <SupplyNoteText $tone={supplierOnTimeTone(item.commercials.onTimePct)}>
              {item.commercials.note}
            </SupplyNoteText>
          )}
        </SupplyLeadWrap>
      ) : (
        <SupplyCellValue>—</SupplyCellValue>
      )),
    },
    {
      id: 'onTime',
      header: 'ON-TIME %',
      cell: (item) => (item.commercials?.onTimePct != null ? (
        <StatusPill status={supplierOnTimeTone(item.commercials.onTimePct)}>
          {`${item.commercials.onTimePct}%`}
        </StatusPill>
      ) : (
        <SupplyCellValue>—</SupplyCellValue>
      )),
    },
    {
      id: 'lastPriceMove',
      header: 'LAST PRICE MOVE',
      cell: (item) => (
        <SupplyCellValue>
          {item.commercials ? item.commercials.lastPriceMoveLabel : '—'}
        </SupplyCellValue>
      ),
    },
    {
      id: 'openOrders',
      header: 'OPEN ORDERS',
      cell: (item) => (item.commercials ? (
        <SupplyOpenOrders $open={item.commercials.openOrdersCount > 0}>
          {`${item.commercials.openOrdersCount} · ${formatSupplyGhs(item.commercials.openOrdersValueGhs)}`}
        </SupplyOpenOrders>
      ) : (
        <SupplyOpenOrders>—</SupplyOpenOrders>
      )),
    },
    { id: 'phoneNumber', header: 'Phone Number', cell: (item) => item.phoneNumber || '/' },
    { id: 'address', header: 'Address', cell: (item) => item.address || '/' },
    { id: 'vatId', header: 'VAT ID', cell: (item) => item.vatId || '/' },
    { id: 'email', header: 'Email', cell: (item) => item.email || '/' },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => (
        <StatusPill status={item.status === 1 ? 'success' : 'neutral'}>
          {statuses[item.status - 1]}
        </StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (item) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Options item={item} handleRemove={() => handleRemove(item.id)} title='Supplier' />
        </span>
      ),
    },
  ]

  return (
    <>
      <NavBar defaultValue={params.q} input placeholder='Search Suppliers...' setParams={setParams} limit={30} />
      <PageWrapper>
        <Flex between center width='100%' mb='16'>
          <MainHeadline>Suppliers</MainHeadline>
          <Button variant='primary' onClick={() => navigate('add')}>
            Add Supplier
          </Button>
        </Flex>


        {rows.length > 0 ? (
          <HonestTable columns={columns} data={rows} keyField='id' />
        ) : isLoading ? (
          <></>
        ) : (
          <NoItems title='suppliers' />
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
